// purpose: R2 本編専用の近未来ミニマルテクノ BGM を、既存 AudioEngine の共有 AudioContext / music bus 上へ決定論的にスケジュールする。

export const R2_MINIMAL_BPM = 134;
export const R2_MINIMAL_STEP_SECONDS = 60 / R2_MINIMAL_BPM / 4;

const SILENCE = 0.0001;
const TWO_BARS = 31;
const ONE_BAR = 15;
const STAGE_CLIMAX = 2;

/**
 * -1 = rest、0 以上 = B1 からの半音オフセット。
 * 旋律ではなくキック間の負圧を作るため、発音位置と音程変化を絞っている。
 */
const SUB_PATTERN = new Int8Array([
  -1, -1, -1, 0,
  -1, -1, 0, -1,
  -1, -1, -1, 0,
  -1, 2, -1, -1,
]);

/** 1小節内の金属クリック。左右位置も固定し、乱数に頼らず微細な運動を作る。 */
const METAL_PATTERN = new Int8Array([
  -1, -1, -1, -72,
  -1, -1, -1, -1,
  -1, -1, 66, -1,
  -1, -1, -1, -1,
]);

/** Stage 3で常時走る非旋律的なレゾナンス列。休符を残し、射撃が主役になる隙間は潰さない。 */
const DRIVE_PATTERN = new Int8Array([
  0, -1, 7, 3,
  -1, 10, -1, 7,
  0, 12, -1, 3,
  7, -1, 10, -1,
]);

/**
 * ボス戦専用の上物。下降グライドやSE的な単発ではなく、2小節で完結する
 * 固定フレーズとしてBGM編成に入る。休符は射撃列が前へ出るための空間。
 */
const BOSS_LEAD_PATTERN = new Int8Array([
  -1, -1, 12, -1,
  -1, 7, -1, 15,
  -1, -1, 10, -1,
  3, -1, 7, 19,
  -1, 10, -1, 15,
  -1, 7, 12, -1,
  -1, 3, -1, 10,
  -1, 15, 7, -1,
]);

const midiToHz = (midi: number): number => 440 * Math.pow(2, (midi - 69) / 12);

/**
 * AudioEngine の16分音符スケジューラから呼ばれる唯一の入口。
 * musicLayer は各ステージ内の密度、stage はラン全体の熱量として加算する。
 * Stage 1は基礎、Stage 2は裏拍と空間、Stage 3以降は16分駆動を最低状態から解放する。
 */
export function scheduleR2MinimalMusicStep(
  context: AudioContext,
  destination: AudioNode,
  noise: AudioBuffer,
  step: number,
  time: number,
  layer: number,
  boss: boolean,
  stage = 0,
): void {
  const within = step & ONE_BAR;
  const phrase = step & 63;
  const stageEnergy = Math.max(0, Math.min(STAGE_CLIMAX, stage | 0));
  const effectiveLayer = Math.min(3, layer + stageEnergy);

  if ((within & 3) === 0) {
    const downbeat = 0.225 + stageEnergy * 0.03;
    const quarter = 0.182 + stageEnergy * 0.018;
    technoKick(context, destination, time, within === 0 ? downbeat : quarter, effectiveLayer >= 3);
  }

  // FOUNDATION: 4つ打ち + 裏拍の乾いたハット + 疎なサブ。
  if ((within & 3) === 2) {
    filteredNoise(context, destination, noise, time, 0.026, effectiveLayer >= 2 ? 0.024 : 0.017, 7600, within < 8 ? -0.28 : 0.28);
  }
  const subOffset = SUB_PATTERN[within] ?? -1;
  if (subOffset >= 0 && (effectiveLayer > 0 || within === 3 || within === 11)) {
    subPulse(context, destination, time, midiToHz(35 + subOffset), effectiveLayer >= 2 ? 0.076 : 0.052);
  }

  // MOTION: 16分ゴーストと左右を往復する金属クリック。
  if (effectiveLayer >= 1) {
    if ((within & 3) === 1) {
      filteredNoise(context, destination, noise, time, 0.014, stageEnergy >= 1 ? 0.008 : 0.0055, 9200, within < 8 ? 0.42 : -0.42);
    }
    // 圧縮ステージの敵密度へ追従する第二ゴースト列。拍裏の主ハットは太らせず、
    // 反対側の16分だけを追加して打点数を倍化する。
    if ((within & 3) === 3) {
      filteredNoise(
        context,
        destination,
        noise,
        time,
        0.018,
        effectiveLayer >= 3 ? 0.010 : 0.0065,
        8400,
        within < 8 ? -0.48 : 0.48,
      );
    }
    const metalPan = METAL_PATTERN[within] ?? -1;
    if (metalPan !== -1) {
      metalTick(context, destination, time, phrase, metalPan / 100, effectiveLayer >= 3 ? 0.028 : 0.018);
    }
  }

  // PRESSURE: 2拍目/4拍目をノイズの気配だけで示し、2小節単位でダブ・スタブを置く。
  if (effectiveLayer >= 2) {
    if (within === 4 || within === 12) {
      technoClap(context, destination, noise, time, within === 4 ? -0.18 : 0.18, stageEnergy);
    }
    if (within === 6 || (phrase > TWO_BARS && within === 14)) {
      dubStab(context, destination, time, phrase > TWO_BARS ? 116.54 : 110, within === 6 ? -0.34 : 0.34);
    }
  }

  // OVERDRIVE: 5ステップ周期を4/4へ重ね、周期が少しずつ位相を変える機械感を作る。
  if (effectiveLayer >= 3 && step % 5 === 0) {
    machinePing(context, destination, time, phrase, step % 10 === 0 ? -0.52 : 0.52);
  }
  if (effectiveLayer >= 3 && within === 14) {
    filteredNoise(context, destination, noise, time, 0.13, 0.022, 6100, 0.18);
  }

  // STAGE 2: オープンハットと広い機械スタブを解放。Stage 1との差を一拍で認識させる。
  if (stageEnergy >= 1) {
    if (within === 7 || within === 15) {
      filteredNoise(context, destination, noise, time, 0.19, stageEnergy >= 2 ? 0.036 : 0.028, 6400, within === 7 ? -0.62 : 0.62);
      subPulse(
        context,
        destination,
        time,
        midiToHz(within === 7 ? 31 : 33),
        stageEnergy >= STAGE_CLIMAX ? 0.067 : 0.052,
      );
    }
    if (within === 5 || within === 13) {
      motorHit(context, destination, time, within, stageEnergy);
    }
    if ((within === 2 || (stageEnergy >= STAGE_CLIMAX && within === 10)) && (phrase < 32 || effectiveLayer >= 3)) {
      stageStab(context, destination, time, phrase, stageEnergy);
    }
  }

  // STAGE 3: 16分レゾナンス列・拍裏リム・4小節頭の衝撃を常設し、曲自体をクライマックス化する。
  if (stageEnergy >= STAGE_CLIMAX) {
    if (within === 0 || within === 8) {
      climaxChord(context, destination, time, within);
    }
    const drive = DRIVE_PATTERN[within] ?? -1;
    if (drive >= 0) {
      resonanceDrive(context, destination, time, midiToHz(45 + drive), within, effectiveLayer);
    }
    if (within === 1 || within === 9) {
      motorHit(context, destination, time, within, stageEnergy);
    }
    if ((within & 3) === 3) {
      metalRim(context, destination, time, within < 8 ? -0.72 : 0.72);
    }
    if (phrase === 0) {
      stageImpact(context, destination, time);
    }
    if (phrase === 60) {
      noiseLift(context, destination, noise, time);
    }
  }

  // BOSS CROWN: 同じ134 BPMの床へ、半小節ごとの巨大コードと固定主旋律を追加。
  // イベントSEではなく、boss=true の間だけ存在するBGMレイヤーとして完結させる。
  if (boss) {
    if (within === 0 || within === 8) {
      bossCrown(context, destination, time, phrase, within, stageEnergy);
    }
    const bossLeadOffset = BOSS_LEAD_PATTERN[step & TWO_BARS] ?? -1;
    if (bossLeadOffset >= 0) {
      bossLead(context, destination, time, phrase, within, bossLeadOffset, stageEnergy);
    }
    if (within === 0) {
      pressureDrone(context, destination, time, phrase > TWO_BARS ? 43.65 : 41.2, stageEnergy);
    }
  }
}

function technoClap(
  context: AudioContext,
  destination: AudioNode,
  noise: AudioBuffer,
  time: number,
  panValue: number,
  stageEnergy: number,
): void {
  filteredNoise(context, destination, noise, time, 0.075, 0.024 + stageEnergy * 0.004, 2250, panValue);
  filteredNoise(context, destination, noise, time + 0.018, 0.045, 0.014 + stageEnergy * 0.003, 4100, -panValue * 0.6);
}

function technoKick(context: AudioContext, destination: AudioNode, time: number, level: number, hard: boolean): void {
  const body = context.createOscillator();
  const bodyEnvelope = context.createGain();
  body.type = 'sine';
  body.frequency.setValueAtTime(hard ? 154 : 142, time);
  body.frequency.exponentialRampToValueAtTime(48, time + 0.045);
  body.frequency.exponentialRampToValueAtTime(36, time + 0.27);
  bodyEnvelope.gain.setValueAtTime(level, time);
  bodyEnvelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.29);
  body.connect(bodyEnvelope);
  bodyEnvelope.connect(destination);
  body.start(time);
  body.stop(time + 0.3);

  const click = context.createOscillator();
  const clickEnvelope = context.createGain();
  click.type = 'triangle';
  click.frequency.setValueAtTime(1900, time);
  click.frequency.exponentialRampToValueAtTime(165, time + 0.018);
  clickEnvelope.gain.setValueAtTime(hard ? 0.026 : 0.019, time);
  clickEnvelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.022);
  click.connect(clickEnvelope);
  clickEnvelope.connect(destination);
  click.start(time);
  click.stop(time + 0.025);
}

function filteredNoise(
  context: AudioContext,
  destination: AudioNode,
  noise: AudioBuffer,
  time: number,
  duration: number,
  level: number,
  frequency: number,
  panValue: number,
): void {
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  source.buffer = noise;
  filter.type = frequency >= 5000 ? 'highpass' : 'bandpass';
  filter.frequency.setValueAtTime(frequency, time);
  filter.Q.setValueAtTime(frequency >= 5000 ? 0.7 : 1.4, time);
  envelope.gain.setValueAtTime(Math.max(SILENCE, level), time);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + duration);
  pan.pan.setValueAtTime(panValue, time);
  source.connect(filter);
  filter.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  source.start(time);
  source.stop(time + duration + 0.01);
}

function subPulse(context: AudioContext, destination: AudioNode, time: number, frequency: number, level: number): void {
  const oscillator = context.createOscillator();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(frequency, time);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(32, frequency * 0.985), time + 0.2);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(180, time);
  filter.frequency.exponentialRampToValueAtTime(72, time + 0.2);
  filter.Q.setValueAtTime(0.8, time);
  envelope.gain.setValueAtTime(SILENCE, time);
  envelope.gain.exponentialRampToValueAtTime(level, time + 0.008);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.21);
  oscillator.connect(filter);
  filter.connect(envelope);
  envelope.connect(destination);
  oscillator.start(time);
  oscillator.stop(time + 0.23);
}

function metalTick(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  phrase: number,
  panValue: number,
  level: number,
): void {
  const first = context.createOscillator();
  const second = context.createOscillator();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  const base = phrase > TWO_BARS ? 521 : 487;
  first.type = 'square';
  second.type = 'triangle';
  first.frequency.setValueAtTime(base, time);
  second.frequency.setValueAtTime(base * 1.493, time);
  envelope.gain.setValueAtTime(level, time);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.055);
  pan.pan.setValueAtTime(panValue, time);
  first.connect(envelope);
  second.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  first.start(time);
  second.start(time);
  first.stop(time + 0.06);
  second.stop(time + 0.06);
}

function dubStab(context: AudioContext, destination: AudioNode, time: number, frequency: number, panValue: number): void {
  const first = context.createOscillator();
  const second = context.createOscillator();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  first.type = 'sawtooth';
  second.type = 'sawtooth';
  first.frequency.setValueAtTime(frequency, time);
  second.frequency.setValueAtTime(frequency * 1.498, time);
  second.detune.setValueAtTime(7, time);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1250, time);
  filter.frequency.exponentialRampToValueAtTime(170, time + 0.19);
  filter.Q.setValueAtTime(7.5, time);
  envelope.gain.setValueAtTime(SILENCE, time);
  envelope.gain.exponentialRampToValueAtTime(0.032, time + 0.006);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.21);
  pan.pan.setValueAtTime(panValue, time);
  first.connect(filter);
  second.connect(filter);
  filter.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  first.start(time);
  second.start(time);
  first.stop(time + 0.23);
  second.stop(time + 0.23);
}

function machinePing(context: AudioContext, destination: AudioNode, time: number, phrase: number, panValue: number): void {
  const oscillator = context.createOscillator();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(phrase > TWO_BARS ? 1294 : 1187, time);
  oscillator.frequency.exponentialRampToValueAtTime(620, time + 0.075);
  envelope.gain.setValueAtTime(0.024, time);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.08);
  pan.pan.setValueAtTime(panValue, time);
  oscillator.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  oscillator.start(time);
  oscillator.stop(time + 0.085);
}

function stageStab(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  phrase: number,
  stageEnergy: number,
): void {
  const low = context.createOscillator();
  const high = context.createOscillator();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  const base = phrase > TWO_BARS ? 130.81 : 123.47;
  low.type = 'sawtooth';
  high.type = 'square';
  low.frequency.setValueAtTime(base, time);
  high.frequency.setValueAtTime(base * 1.498, time);
  high.detune.setValueAtTime(9, time);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(stageEnergy >= STAGE_CLIMAX ? 4100 : 2900, time);
  filter.frequency.exponentialRampToValueAtTime(340, time + 0.17);
  filter.Q.setValueAtTime(8.2, time);
  envelope.gain.setValueAtTime(SILENCE, time);
  envelope.gain.exponentialRampToValueAtTime(stageEnergy >= STAGE_CLIMAX ? 0.057 : 0.043, time + 0.004);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.2);
  pan.pan.setValueAtTime(phrase > TWO_BARS ? 0.42 : -0.42, time);
  low.connect(filter);
  high.connect(filter);
  filter.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  low.start(time);
  high.start(time);
  low.stop(time + 0.21);
  high.stop(time + 0.21);
}

function motorHit(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  within: number,
  stageEnergy: number,
): void {
  const body = context.createOscillator();
  const edge = context.createOscillator();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  const base = (within & 8) === 0 ? 214 : 184;
  body.type = 'triangle';
  edge.type = 'square';
  body.frequency.setValueAtTime(base, time);
  body.frequency.exponentialRampToValueAtTime(base * 0.72, time + 0.085);
  edge.frequency.setValueAtTime(base * 1.61, time);
  edge.frequency.exponentialRampToValueAtTime(base * 1.18, time + 0.055);
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(stageEnergy >= STAGE_CLIMAX ? 760 : 620, time);
  filter.Q.setValueAtTime(2.7, time);
  envelope.gain.setValueAtTime(stageEnergy >= STAGE_CLIMAX ? 0.044 : 0.032, time);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.095);
  pan.pan.setValueAtTime((within & 8) === 0 ? -0.5 : 0.5, time);
  body.connect(filter);
  edge.connect(filter);
  filter.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  body.start(time);
  edge.start(time);
  body.stop(time + 0.1);
  edge.stop(time + 0.1);
}

function climaxChord(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  within: number,
): void {
  const low = context.createOscillator();
  const mid = context.createOscillator();
  const high = context.createOscillator();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  const base = within === 0 ? 82.41 : 87.31;
  low.type = 'sawtooth';
  mid.type = 'square';
  high.type = 'triangle';
  low.frequency.setValueAtTime(base, time);
  mid.frequency.setValueAtTime(base * 1.493, time);
  high.frequency.setValueAtTime(base * 2.147, time);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(4300, time);
  filter.frequency.exponentialRampToValueAtTime(310, time + 0.24);
  filter.Q.setValueAtTime(6.8, time);
  envelope.gain.setValueAtTime(SILENCE, time);
  envelope.gain.exponentialRampToValueAtTime(0.052, time + 0.004);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.26);
  pan.pan.setValueAtTime(within === 0 ? -0.28 : 0.28, time);
  low.connect(filter);
  mid.connect(filter);
  high.connect(filter);
  filter.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  low.start(time);
  mid.start(time);
  high.start(time);
  low.stop(time + 0.27);
  mid.stop(time + 0.27);
  high.stop(time + 0.27);
}

function resonanceDrive(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  frequency: number,
  within: number,
  layer: number,
): void {
  const oscillator = context.createOscillator();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  oscillator.type = 'sawtooth';
  oscillator.frequency.setValueAtTime(frequency, time);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(layer >= 3 ? 4200 : 3100, time);
  filter.frequency.exponentialRampToValueAtTime(620, time + 0.075);
  filter.Q.setValueAtTime(10.5, time);
  envelope.gain.setValueAtTime(SILENCE, time);
  envelope.gain.exponentialRampToValueAtTime(layer >= 3 ? 0.039 : 0.031, time + 0.002);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.082);
  pan.pan.setValueAtTime((within & 2) === 0 ? -0.34 : 0.34, time);
  oscillator.connect(filter);
  filter.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  oscillator.start(time);
  oscillator.stop(time + 0.09);
}

function metalRim(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  panValue: number,
): void {
  const first = context.createOscillator();
  const second = context.createOscillator();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  first.type = 'triangle';
  second.type = 'square';
  first.frequency.setValueAtTime(1380, time);
  second.frequency.setValueAtTime(2197, time);
  envelope.gain.setValueAtTime(0.024, time);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.036);
  pan.pan.setValueAtTime(panValue, time);
  first.connect(envelope);
  second.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  first.start(time);
  second.start(time);
  first.stop(time + 0.04);
  second.stop(time + 0.04);
}

function stageImpact(context: AudioContext, destination: AudioNode, time: number): void {
  const body = context.createOscillator();
  const edge = context.createOscillator();
  const envelope = context.createGain();
  const filter = context.createBiquadFilter();
  body.type = 'sine';
  edge.type = 'sawtooth';
  body.frequency.setValueAtTime(92, time);
  body.frequency.exponentialRampToValueAtTime(32, time + 0.42);
  edge.frequency.setValueAtTime(196, time);
  edge.frequency.exponentialRampToValueAtTime(49, time + 0.2);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1400, time);
  filter.frequency.exponentialRampToValueAtTime(105, time + 0.34);
  filter.Q.setValueAtTime(3.4, time);
  envelope.gain.setValueAtTime(0.105, time);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.46);
  body.connect(envelope);
  edge.connect(filter);
  filter.connect(envelope);
  envelope.connect(destination);
  body.start(time);
  edge.start(time);
  body.stop(time + 0.48);
  edge.stop(time + 0.48);
}

function noiseLift(
  context: AudioContext,
  destination: AudioNode,
  noise: AudioBuffer,
  time: number,
): void {
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  source.buffer = noise;
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(680, time);
  filter.frequency.exponentialRampToValueAtTime(7200, time + 0.43);
  filter.Q.setValueAtTime(1.35, time);
  envelope.gain.setValueAtTime(SILENCE, time);
  envelope.gain.exponentialRampToValueAtTime(0.034, time + 0.34);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 0.45);
  pan.pan.setValueAtTime(-0.65, time);
  pan.pan.linearRampToValueAtTime(0.65, time + 0.43);
  source.connect(filter);
  filter.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  source.start(time);
  source.stop(time + 0.47);
}

function bossCrown(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  phrase: number,
  within: number,
  stageEnergy: number,
): void {
  const low = context.createOscillator();
  const body = context.createOscillator();
  const edge = context.createOscillator();
  const air = context.createOscillator();
  const leftFilter = context.createBiquadFilter();
  const rightFilter = context.createBiquadFilter();
  const leftEnvelope = context.createGain();
  const rightEnvelope = context.createGain();
  const leftPan = context.createStereoPanner();
  const rightPan = context.createStereoPanner();
  const secondHalf = phrase > TWO_BARS;
  const base = (within === 0 ? 92.5 : 98) * (secondHalf ? 1.059 : 1);
  const peak = 0.041 + stageEnergy * 0.006;
  const duration = within === 0 ? 0.78 : 0.61;

  low.type = 'sawtooth';
  body.type = 'square';
  edge.type = 'sawtooth';
  air.type = 'triangle';
  low.frequency.setValueAtTime(base, time);
  body.frequency.setValueAtTime(base * 1.498, time);
  body.detune.setValueAtTime(-7, time);
  edge.frequency.setValueAtTime(base * 2.012, time);
  edge.detune.setValueAtTime(8, time);
  air.frequency.setValueAtTime(base * 2.672, time);

  leftFilter.type = 'lowpass';
  rightFilter.type = 'lowpass';
  leftFilter.frequency.setValueAtTime(5200 + stageEnergy * 650, time);
  rightFilter.frequency.setValueAtTime(6100 + stageEnergy * 700, time);
  leftFilter.frequency.exponentialRampToValueAtTime(430, time + duration * .82);
  rightFilter.frequency.exponentialRampToValueAtTime(620, time + duration * .76);
  leftFilter.Q.setValueAtTime(5.8, time);
  rightFilter.Q.setValueAtTime(7.1, time);

  leftEnvelope.gain.setValueAtTime(SILENCE, time);
  rightEnvelope.gain.setValueAtTime(SILENCE, time);
  leftEnvelope.gain.exponentialRampToValueAtTime(peak, time + .014);
  rightEnvelope.gain.exponentialRampToValueAtTime(peak * .92, time + .021);
  leftEnvelope.gain.exponentialRampToValueAtTime(SILENCE, time + duration);
  rightEnvelope.gain.exponentialRampToValueAtTime(SILENCE, time + duration);
  leftPan.pan.setValueAtTime(-.48, time);
  rightPan.pan.setValueAtTime(.48, time);

  low.connect(leftFilter);
  edge.connect(leftFilter);
  body.connect(rightFilter);
  air.connect(rightFilter);
  leftFilter.connect(leftEnvelope);
  rightFilter.connect(rightEnvelope);
  leftEnvelope.connect(leftPan);
  rightEnvelope.connect(rightPan);
  leftPan.connect(destination);
  rightPan.connect(destination);
  low.start(time);
  body.start(time);
  edge.start(time);
  air.start(time);
  low.stop(time + duration + .01);
  body.stop(time + duration + .01);
  edge.stop(time + duration + .01);
  air.stop(time + duration + .01);
}

function bossLead(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  phrase: number,
  within: number,
  offset: number,
  stageEnergy: number,
): void {
  const body = context.createOscillator();
  const edge = context.createOscillator();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  const pan = context.createStereoPanner();
  const root = phrase > TWO_BARS ? 50 : 48;
  const frequency = midiToHz(root + offset);
  const duration = within === 15 ? .29 : .18;

  body.type = 'sawtooth';
  edge.type = 'square';
  body.frequency.setValueAtTime(frequency, time);
  edge.frequency.setValueAtTime(frequency * 1.503, time);
  edge.detune.setValueAtTime((within & 4) === 0 ? -9 : 9, time);
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(2600 + stageEnergy * 420, time);
  filter.frequency.exponentialRampToValueAtTime(760, time + duration);
  filter.Q.setValueAtTime(4.8, time);
  envelope.gain.setValueAtTime(SILENCE, time);
  envelope.gain.exponentialRampToValueAtTime(.034 + stageEnergy * .005, time + .005);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + duration);
  pan.pan.setValueAtTime((within & 4) === 0 ? -.38 : .38, time);
  body.connect(filter);
  edge.connect(filter);
  filter.connect(envelope);
  envelope.connect(pan);
  pan.connect(destination);
  body.start(time);
  edge.start(time);
  body.stop(time + duration + .01);
  edge.stop(time + duration + .01);
}

function pressureDrone(
  context: AudioContext,
  destination: AudioNode,
  time: number,
  frequency: number,
  stageEnergy: number,
): void {
  const oscillator = context.createOscillator();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  oscillator.type = 'sawtooth';
  oscillator.frequency.setValueAtTime(frequency, time);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(115 + stageEnergy * 32, time);
  filter.Q.setValueAtTime(2.2 + stageEnergy * 0.7, time);
  envelope.gain.setValueAtTime(SILENCE, time);
  envelope.gain.exponentialRampToValueAtTime(0.021 + stageEnergy * 0.006, time + 0.12);
  envelope.gain.exponentialRampToValueAtTime(SILENCE, time + 1.65);
  oscillator.connect(filter);
  filter.connect(envelope);
  envelope.connect(destination);
  oscillator.start(time);
  oscillator.stop(time + 1.7);
}
